import { AssignKioskApi } from './assign-kiosk-api';
import {
  MachineModelResponse,
  MachineDefaultCreditResponse,
  KioskDetailsResponse,
  KioskLocation,
  KioskEvse,
  KioskLocationsResponse,
  KioskConnector,
  ConnectorSetting,
  KioskScreenType,
  SparkAssignableLocation,
  VpostMappingResponse,
  VpostMappingRequest,
  RemoteStartMappingData,
} from '../types/assign-kiosk-types';

/**
 * Repository layer for the assign-kiosk feature.
 * Translates raw API responses into domain objects and handles
 * merging of saved connector settings from kiosk details.
 */
export class AssignKioskRepository {
  constructor(private readonly api: AssignKioskApi) {}

  /** API #1 — machine model (determines screen type). */
  async getMachineModel(machineId: string): Promise<MachineModelResponse> {
    return this.api.getMachineModel(machineId);
  }

  /** Live Lynx Default Credit + currency for the machine. */
  async getMachineDefaultCredit(machineId: string): Promise<MachineDefaultCreditResponse> {
    return this.api.getMachineDefaultCredit(machineId);
  }

  /** API #2 — current kiosk assignment. */
  async getKioskDetails(machineId: string): Promise<KioskDetailsResponse> {
    return this.api.getKioskDetails(machineId);
  }

  /**
   * API #3 — locations for the operator (or by machineId as fallback).
   * logic: use operatorId if available, otherwise machineId.
   */
  async getLocations(
    operatorId: string | null,
    machineId: string | null,
    savedSettings: string | null = null,
    locationId?: string
  ): Promise<KioskLocation[]> {
    let response: KioskLocationsResponse;
    if (operatorId) {
      response = await this.api.getLocationsByOperatorId(operatorId, locationId);
    } else if (machineId) {
      response = await this.api.getLocationsByMachineId(machineId);
    } else {
      throw new Error('Either operatorId or machineId is required to fetch locations');
    }

    const locations: KioskLocation[] = Array.isArray(response) ? response : [];
    const settingsMap = this.parseSettingsMap(savedSettings);
    const enriched = locations.map((loc) => ({
      ...loc,
      evses: loc.evses.map((evse) => ({
        ...evse,
        connectors: evse.connectors.map((conn) => ({
          ...conn,
          physicalReference: conn.physicalReference ?? evse.physicalReference ?? null,
        })),
      })),
    }));
    return enriched.map((loc) => this.mergeSettings(loc, settingsMap));
  }

  /**
   * API #4 — register / assign kiosk to a location.
   * The settingsJson must already be the double-serialized string produced by
   * the buildSettingsJson utility.
   */
  async registerKiosk(
    machineId: string,
    countryCode: string,
    partyId: string,
    locationId: string,
    settingsJson: string
  ): Promise<void> {
    return this.api.registerKiosk(machineId, countryCode, partyId, locationId, settingsJson);
  }

  // ─── SPARK_DEVICE_START flow ──────────────────────────────────────────────

  /**
   * SPARK #1+#2 combined — fetches assignable locations and the current mapping,
   * then merges them into the common KioskLocation[] shape.
   *
   * Each SPARK EVSE becomes a KioskEvse with a single synthetic connector (id='1').
   * The mapping response drives isSelected, displayOrder, and alias.
   */
  async getSparkLocations(machineId: string): Promise<{ locations: KioskLocation[]; assignedLocationId: string | null }> {
    const mapping = await this.api.getSparkDeviceMapping(machineId).catch(() => null);
    const assignableLocations = await this.api.getAssignableLocationsEvses(machineId);

    // Build a map of evseUid → mapping entry for fast lookup
    const mappingMap = new Map<string, VpostMappingResponse['evses_info'][0]>();
    if (mapping) {
      for (const entry of mapping.evses_info) {
        mappingMap.set(entry.evse_uid, entry);
      }
    }

    const locations: KioskLocation[] = (assignableLocations ?? []).map((sparkLoc: SparkAssignableLocation) => ({
      id: sparkLoc.location_id,
      name: sparkLoc.location_name,
      countryCode: '',
      partyId: '',
      evses: sparkLoc.evses.map((sparkEvse): KioskEvse => {
        const mapped = mappingMap.get(sparkEvse.uid);
        const connector: KioskConnector = {
          id: '1',
          status: sparkEvse.status,
          powerType: sparkEvse.power_type,
          physicalReference: sparkEvse.physical_reference,
          alias: mapped?.nickname ?? mapped?.mapped_button_label ?? sparkEvse.nickname ?? sparkEvse.mapped_button_label ?? '',
          isSelected: !!mapped,
          displayOrder: mapped?.assigned_order ?? null,
        };
        return {
          uid: sparkEvse.uid,
          id: sparkEvse.id,
          status: sparkEvse.status,
          connectors: [connector],
        };
      }),
    }));

    return {
      locations,
      assignedLocationId: mapping?.location_id ?? null,
    };
  }

  // ─── SPARK_REMOTE_START flow ──────────────────────────────────────────────

  /**
   * REMOTE #1+#2 combined — same assignable-locations-evses endpoint, but uses
   * the minimal mapping endpoint (no /with-labels).
   * Response shape: { location_id, evse_uids: string[] }
   */
  async getRemoteStartLocations(machineId: string): Promise<{ locations: KioskLocation[]; assignedLocationId: string | null }> {
    const mapping = await this.api.getRemoteStartMapping(machineId).catch(() => null);
    const assignableLocations = await this.api.getAssignableLocationsEvses(machineId);

    const selectedUids = new Set<string>(mapping?.evse_uids ?? []);

    const locations: KioskLocation[] = (assignableLocations ?? []).map((sparkLoc: SparkAssignableLocation) => ({
      id: sparkLoc.location_id,
      name: sparkLoc.location_name,
      countryCode: '',
      partyId: '',
      evses: sparkLoc.evses.map((sparkEvse): KioskEvse => ({
        uid: sparkEvse.uid,
        id: sparkEvse.id,
        status: sparkEvse.status,
        connectors: [{
          id: '1',
          status: sparkEvse.status,
          powerType: sparkEvse.power_type,
          physicalReference: sparkEvse.physical_reference,
          alias: sparkEvse.nickname ?? sparkEvse.mapped_button_label ?? '',
          isSelected: selectedUids.has(sparkEvse.uid),
          displayOrder: null,
        }],
      })),
    }));

    return {
      locations,
      assignedLocationId: mapping?.location_id ?? null,
    };
  }

  /**
   * REMOTE #3 — saves the remote-start mapping (evse_uids only, no labels).
   */
  async saveRemoteStartMapping(machineId: string, location: KioskLocation): Promise<void> {
    const body: RemoteStartMappingData = {
      location_id: location.id,
      evse_uids: location.evses
        .filter((evse) => evse.connectors[0]?.isSelected)
        .map((evse) => evse.uid),
    };
    return this.api.putRemoteStartMapping(machineId, body);
  }

  /**
   * SPARK #3 — saves the SPARK device mapping from the selected KioskLocation.
   */
  async saveSparkMapping(machineId: string, location: KioskLocation): Promise<void> {
    const body: VpostMappingRequest = {
      location_id: location.id,
      evses_info: location.evses
        .filter((evse) => evse.connectors[0]?.isSelected)
        .map((evse) => ({
          evse_uid: evse.uid,
          assigned_order: evse.connectors[0]?.displayOrder ?? null,
          nickname: evse.connectors[0]?.alias || null,
          mapped_button_label: evse.connectors[0]?.alias || null,
        })),
    };
    return this.api.putSparkDeviceMapping(machineId, body);
  }

  /**
   * Derives the screen type from a machine-model string.
   * "EV_KIOSK" → EV_KIOSK, anything that looks OCPI-related → OCPI, else FALLBACK.
   */
  resolveScreenType(machineModel: string | null | undefined): KioskScreenType {
    if (!machineModel) return 'FALLBACK';
    if (machineModel === 'EV_KIOSK') return 'EV_KIOSK';
    if (machineModel === 'SPARK_DEVICE_START') return 'SPARK_DEVICE_START';
    if (machineModel === 'SPARK_REMOTE_START') return 'SPARK_REMOTE_START';
    // Reserve OCPI for a future ticket
    if (machineModel.toUpperCase().includes('OCPI')) return 'OCPI';
    if (machineModel === 'UNKNOWN') return 'UNKNOWN';
    return 'FALLBACK';
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /**
   * Parses the settings JSON string into a Map keyed by composite key
   * "{evseUid}:{connectorId}".
   */
  private parseSettingsMap(
    settings: string | null
  ): Map<string, ConnectorSetting> {
    const map = new Map<string, ConnectorSetting>();
    if (!settings) return map;

    try {
      const parsed = JSON.parse(settings) as { filter?: ConnectorSetting[] };
      for (const item of parsed.filter ?? []) {
        const key = `${item.evseUid}:${item.connectorId}`;
        map.set(key, item);
      }
    } catch {
    }

    return map;
  }

  /** Merges saved connector settings into a location's EVSEs and connectors. */
  private mergeSettings(
    location: KioskLocation,
    settingsMap: Map<string, ConnectorSetting>
  ): KioskLocation {
    return {
      ...location,
      evses: location.evses.map((evse) => ({
        ...evse,
        connectors: evse.connectors.map((connector): KioskConnector => {
          const key = `${evse.uid}:${connector.id}`;
          const saved = settingsMap.get(key);
          return {
            ...connector,
            alias: saved?.alias ?? '',
            isSelected: saved?.isActive ?? false,
            displayOrder: saved?.index ?? null,
          };
        }),
      })),
    };
  }
}
